import React from 'react';
import PropTypes from 'prop-types';
import { SettingsIcon, KeyIcon, TrashIcon, CopyIcon } from '../../assets/icons';
import { SettingsPanel, OutlineButton } from './AccountSections';

const inputClassName =
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50';

export default function AccountRampSettings({
  apiKeys,
  apiKeysLoading,
  newApiKeyName,
  copyFeedback,
  webhookForm,
  onApiKeyNameChange,
  onCreateApiKey,
  onDeleteApiKey,
  onCopyToClipboard,
  onWebhookFormChange,
  onWebhookEventChange,
}) {
  return (
    <div className='col-span-1 flex flex-col gap-[22px] md:col-span-2'>
      <SettingsPanel title='API keys' id='account-section-ramp'>
        <div className='space-y-4 p-[22px]'>
          {apiKeysLoading && <p className='text-sm text-[var(--text-secondary)]'>Loading API keys...</p>}
          {!apiKeysLoading && (!apiKeys || apiKeys.length === 0) && (
            <p className='text-sm text-[var(--text-secondary)]'>No API keys found</p>
          )}
          {!apiKeysLoading &&
            apiKeys &&
            apiKeys.length > 0 &&
            apiKeys.map((apiKey) => (
              <div
                key={apiKey.value}
                className='flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[var(--color-border)] bg-[var(--off)]/50 p-4'
              >
                <div className='flex min-w-0 items-center gap-3'>
                  <div className='flex h-8 w-8 items-center justify-center rounded-full bg-[var(--n50)]'>
                    <KeyIcon className='h-4 w-4 text-[var(--text-tertiary)]' />
                  </div>
                  <div>
                    <div className='text-sm font-medium text-[var(--n900)]'>{apiKey.name}</div>
                    <div className='flex items-center gap-2'>
                      <div className='text-sm text-[var(--text-secondary)]'>Token: {apiKey.value.substring(0, 8)}...</div>
                      <button
                        type='button'
                        onClick={() => onCopyToClipboard(apiKey.value, `apiKey-${apiKey.value}`)}
                        className='text-[var(--text-tertiary)] hover:text-[var(--n800)]'
                        aria-label={`Copy API key ${apiKey.name}`}
                      >
                        <CopyIcon className='h-4 w-4' />
                      </button>
                      {copyFeedback[`apiKey-${apiKey.value}`] && <span className='text-xs text-[var(--n800)]'>Copied!</span>}
                    </div>
                  </div>
                </div>
                <div className='flex items-center gap-4'>
                  <div className='text-sm text-[var(--text-tertiary)]'>Never used</div>
                  <button
                    type='button'
                    onClick={() => onDeleteApiKey(apiKey)}
                    className='text-[var(--text-tertiary)] hover:text-[var(--color-error)]'
                    aria-label={`Delete API key ${apiKey.name}`}
                  >
                    <TrashIcon className='h-4 w-4' />
                  </button>
                </div>
              </div>
            ))}

          <div className='border-t border-[var(--color-border)] pt-4'>
            <h4 className='mb-2 text-sm font-medium text-[var(--n900)]'>Create new API key</h4>
            <form onSubmit={onCreateApiKey} className='flex flex-wrap gap-2'>
              <input
                type='text'
                placeholder='Enter key name'
                value={newApiKeyName}
                onChange={(e) => onApiKeyNameChange(e.target.value)}
                className={`${inputClassName} min-w-[200px] flex-1`}
              />
              <OutlineButton submit disabled={!newApiKeyName.trim() || apiKeysLoading}>
                Create
              </OutlineButton>
            </form>
          </div>
        </div>
      </SettingsPanel>

      <SettingsPanel title='Atrya webhooks'>
        <div className='space-y-6 p-[22px]'>
          <div className='rounded-lg bg-[var(--off)] p-4'>
            <div className='flex flex-wrap items-center justify-between gap-3'>
              <span className='text-sm font-medium text-[var(--n900)]'>https://notifications-api.dev.caplend.de/api/atrya</span>
              <div className='flex flex-wrap items-center gap-2 text-sm text-[var(--text-tertiary)]'>
                <span className='rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800'>Enabled</span>
                <span>4 events</span>
                <span>Last event 15 hours ago</span>
              </div>
            </div>
          </div>

          <div className='space-y-4'>
            <h4 className='text-base font-semibold text-[var(--n900)]'>New webhook</h4>
            <div className='space-y-2'>
              <label htmlFor='endpointUrl' className='text-sm font-medium text-[var(--n900)]'>
                Endpoint URL*
              </label>
              <input
                id='endpointUrl'
                type='url'
                placeholder='https://your-domain.com/webhook'
                value={webhookForm.endpointUrl}
                onChange={(e) => onWebhookFormChange(e.target.value)}
                className={inputClassName}
              />
            </div>
            <p className='text-sm text-[var(--text-tertiary)]'>Leave all options untoggled to receive all events.</p>
            <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
              {[
                ['crossBorderQuoteNew', 'crossBorder.quote.new'],
                ['crossBorderExecuteNew', 'crossBorder.execute.new'],
                ['recipientNew', 'recipient.new'],
                ['bankAccountNew', 'bankAccount.new'],
                ['payoutUpdate', 'payout.update'],
                ['payoutPartnerFee', 'payout.partnerFee'],
                ['payinNew', 'payin.new'],
                ['payinComplete', 'payin.complete'],
                ['crossBorderExecuteUpdate', 'crossBorder.execute.update'],
                ['crossBorderExecuteComplete', 'crossBorder.execute.complete'],
                ['recipientUpdate', 'recipient.update'],
                ['payoutNew', 'payout.new'],
                ['payoutComplete', 'payout.complete'],
                ['blockchainWalletNew', 'blockchainWallet.new'],
                ['payinUpdate', 'payin.update'],
                ['payinPartnerFee', 'payin.partnerFee'],
              ].map(([key, label]) => (
                <label key={key} htmlFor={key} className='flex cursor-pointer items-center gap-2'>
                  <input
                    id={key}
                    type='checkbox'
                    checked={webhookForm.events[key]}
                    onChange={(e) => onWebhookEventChange(key, e.target.checked)}
                    className='rounded border-gray-300'
                  />
                  <span className='text-sm text-[var(--n900)]'>{label}</span>
                </label>
              ))}
            </div>
            <div className='flex flex-wrap items-center justify-between gap-3 pt-2'>
              <button type='button' className='text-sm text-[var(--n800)] underline-offset-2 hover:underline'>
                Learn more about Atrya webhooks
              </button>
              <OutlineButton>
                <SettingsIcon className='h-3.5 w-3.5' />
                Create
              </OutlineButton>
            </div>
          </div>
        </div>
      </SettingsPanel>
    </div>
  );
}

AccountRampSettings.propTypes = {
  apiKeys: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, value: PropTypes.string })),
  apiKeysLoading: PropTypes.bool,
  newApiKeyName: PropTypes.string.isRequired,
  copyFeedback: PropTypes.shape({}),
  webhookForm: PropTypes.shape({
    endpointUrl: PropTypes.string,
    events: PropTypes.shape({}),
  }).isRequired,
  onApiKeyNameChange: PropTypes.func.isRequired,
  onCreateApiKey: PropTypes.func.isRequired,
  onDeleteApiKey: PropTypes.func.isRequired,
  onCopyToClipboard: PropTypes.func.isRequired,
  onWebhookFormChange: PropTypes.func.isRequired,
  onWebhookEventChange: PropTypes.func.isRequired,
};

AccountRampSettings.defaultProps = {
  apiKeys: [],
  apiKeysLoading: false,
  copyFeedback: {},
};
